import { CSSProperties } from 'react';

export type VideoPrimaryLayout = {
  max_height?: number | null;
  max_height_mobile?: number | null;
  max_height_desktop?: number | null;
};

type VideoWrapperStyleOptions = {
  aspectRatio?: string;
  posterUrl?: string | null;
  /** Solid fill while the video buffers when there is no poster image. */
  noPosterFill?: string;
};

/**
 * Wrapper style resolved on the server: reserves space via aspect-ratio and
 * paints the poster instantly (background) to avoid a blank hero on load.
 * Max heights are exposed as CSS vars consumed by media queries in SCSS,
 * mirroring the Banner/HeroImage pattern (no useIsMobile).
 */
export const getVideoWrapperStyle = (
  primary: VideoPrimaryLayout,
  options: VideoWrapperStyleOptions = {},
): CSSProperties => {
  // heroVideo only has `max_height`; default has explicit mobile/desktop.
  const mobile = primary.max_height_mobile ?? primary.max_height;
  const desktop = primary.max_height_desktop ?? primary.max_height;
  const { aspectRatio, posterUrl, noPosterFill } = options;

  return {
    ...(aspectRatio && { aspectRatio }),
    ...(posterUrl && {
      backgroundImage: `url(${posterUrl})`,
      backgroundPosition: 'center',
      backgroundSize: 'cover',
    }),
    ...(!posterUrl && noPosterFill && { backgroundColor: noPosterFill }),
    ...(mobile && {
      '--video-max-height-mobile': `${mobile}px`,
    }),
    ...(desktop && {
      '--video-max-height-desktop': `${desktop}px`,
    }),
  } as CSSProperties;
};

export const hasVideoMaxHeight = (primary: VideoPrimaryLayout): boolean =>
  Boolean(
    primary.max_height ||
      primary.max_height_mobile ||
      primary.max_height_desktop,
  );
